import {
  AdditiveBlending,
  Color,
  DoubleSide,
  Group,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Object3D,
  PointLight,
  SphereGeometry,
  Vector3,
} from 'three';
import { Layer } from './Layer.js';
import { createTelescopeGeometry } from '../geometry/telescope.js';

/**
 * "…where perfect telescopes…are made."
 *
 * Nine instruments stand along the descent, each with its lantern. Rest your
 * attention on one and the brass warms; press and hold, and the traveller
 * puts an eye to it — the frame closes to a circle, the field narrows, and
 * out there is the city as it was wanted, which is not quite the city that
 * is there.
 *
 * Letting go returns you to the stair, a little later than you left it.
 */
export class Telescopes extends Layer {
  static id = 'telescopes';
  static order = 50;

  async build() {
    const { settings, palette, helix, bus } = this.ctx;
    const cfg = settings.telescopes;
    const s = settings.stair;

    const parts = createTelescopeGeometry();

    this.brassMat = new MeshStandardMaterial({
      color: palette.get('brass').clone(),
      roughness: 0.28,
      metalness: 0.92,
      emissive: new Color(0x000000),
      // The tube is an open shell, so it has to be lit from the inside too —
      // which is where the traveller's eye spends its time.
      side: DoubleSide,
    });
    this.ironMat = new MeshStandardMaterial({
      color: new Color(0x2b2721),
      roughness: 0.62,
      metalness: 0.7,
    });
    this.lensMat = new MeshBasicMaterial({
      color: palette.get('memory').clone(),
      transparent: true,
      opacity: 0.55,
      blending: AdditiveBlending,
      depthWrite: false,
    });
    this.lampMat = new MeshBasicMaterial({
      color: palette.get('lamp').clone(),
      transparent: true,
      opacity: 0.95,
      toneMapped: false,
    });

    this.items = [];
    // A lantern has to be legible from a turn away, or no one ever finds a
    // telescope: it is the only wayfinding in the whole descent.
    const lampGeo = new SphereGeometry(0.13, 16, 12);

    const openings = this.ctx.openings ?? [];
    const outRadius = s.outerRadius - 1.45;
    const inRadius = s.innerRadius + 1.0;

    // Half the instruments look out, and those can only stand where the
    // stair happens to pass an arch at eye level. Those places are found
    // first (see `windowStations`); the rest are spaced along what is left.
    const outCount = Math.ceil(cfg.count / 2);
    const placements = chooseStations(
      windowStations(helix, openings, cfg),
      outCount,
      cfg,
    ).map((st) => ({ outward: true, t: st.t, opening: st.opening }));

    const inCount = cfg.count - placements.length;
    for (let k = 0; k < inCount; k++) {
      const u = (k + 0.5) / inCount;
      placements.push({
        outward: false,
        t: cfg.firstAt + u * (cfg.lastAt - cfg.firstAt),
        opening: null,
      });
    }
    placements.sort((p, q) => p.t - q.t);

    for (let i = 0; i < placements.length; i++) {
      const { outward, t, opening } = placements[i];
      const radius = outward ? outRadius : inRadius;

      const a = helix.angleAt(t);
      const cos = Math.cos(a);
      const sin = Math.sin(a);
      const y = helix.heightAt(t);

      const g = new Group();
      // The mount's feet sit 1.5 below the local origin, so this stands the
      // instrument on the tread rather than through it.
      g.position.set(cos * radius, y + 1.52, sin * radius);

      const brass = new Mesh(parts.brass, this.brassMat);
      const iron = new Mesh(parts.iron, this.ironMat);
      // The objective gets its own material so one instrument can be warmer
      // than its neighbours.
      const lens = new Mesh(parts.lens, this.lensMat.clone());
      g.add(brass, iron, lens);

      // Aim. An outward instrument sights through its arch and keeps going,
      // out over the plain where the dreamed city is; an inward one looks
      // straight down the well, which at this hour is the same thing.
      const aimAt = new Vector3();
      if (outward && opening) {
        // The arch is only a metre or so away, so aiming *at* it would tip
        // the tube steeply. Take the bearing from the arch, then put the
        // target far out on the plain with a gentle declination.
        const bx = opening.x - g.position.x;
        const bz = opening.z - g.position.z;
        const bl = Math.hypot(bx, bz) || 1;
        aimAt.set(
          g.position.x + (bx / bl) * 260,
          g.position.y - 36,
          g.position.z + (bz / bl) * 260,
        );
      } else if (outward) {
        aimAt.set(cos * 200, y - 34, sin * 200);
      } else {
        // The other half look back up the well, over the turns already
        // walked. Down is blocked by the tread they are standing on — and
        // up is the better half of the pair anyway: one instrument for what
        // is wanted, one for what has already happened.
        aimAt.set(-cos * 1.5, y + 78, -sin * 1.5);
      }
      // Object3D.lookAt aims +Z at the target; the instrument sights along
      // -Z, so it is turned to face the other way afterwards.
      const look = new Object3D();
      look.position.copy(g.position);
      look.lookAt(aimAt);
      g.quaternion.copy(look.quaternion);
      g.rotateY(Math.PI);

      // Where an eye would go: *inside* the draw tube, past the closed
      // eyepiece end, which is otherwise a brass disc 10 cm from the lens of
      // the camera. A camera inherits this with no rotation of its own, so
      // its -Z runs straight down the tube and out of the objective.
      // Far enough down the tube that the brass does not frame the view —
      // the composite's iris does that, and two frames is one too many.
      const eye = new Object3D();
      eye.position.set(0, 0, -1.02);
      g.add(eye);

      // The lantern that makes the station findable from a turn away.
      const lamp = new Mesh(lampGeo, this.lampMat);
      lamp.position.set(cos * (radius + (outward ? 0.95 : -0.95)), y + 1.95, sin * (radius + (outward ? 0.95 : -0.95)));
      lamp.renderOrder = 2;
      this.group.add(lamp);

      // Kept small on purpose: a big additive sphere stops being a glow and
      // becomes a pale disc pasted over the architecture.
      const halo = new Mesh(
        new SphereGeometry(0.28, 14, 10),
        new MeshBasicMaterial({
          color: palette.get('lamp').clone(),
          transparent: true,
          opacity: 0.16,
          blending: AdditiveBlending,
          depthWrite: false,
        }),
      );
      halo.position.copy(lamp.position);
      this.group.add(halo);

      this.group.add(g);
      this.items.push({
        index: i,
        t,
        group: g,
        brass,
        iron,
        lens,
        eye,
        lamp,
        halo,
        outward,
        warmth: 0,
        remembered: false,
        baseY: y + 1.12,
      });
    }

    // Two pooled lights that follow whichever lanterns are nearest.
    this.lights = [new PointLight(0xffb470, 0, 26, 1.8), new PointLight(0xffb470, 0, 26, 1.8)];
    for (const l of this.lights) this.group.add(l);

    this.active = null;

    this.ctx.gaze.register({
      kind: 'telescope',
      mode: 'mesh',
      maxDistance: 26,
      objects: () => this.items.map((it) => it.group),
      owner: (obj) => {
        let o = obj;
        while (o && !this.items.some((it) => it.group === o)) o = o.parent;
        return o;
      },
      resolve: (owner) => ({ item: this.items.find((it) => it.group === owner) }),
    });

    bus.on('remember', (e) => {
      if (e.kind !== 'telescope') return;
      const item = e.data?.item;
      if (!item || item.remembered) return;
      item.remembered = true;
      this.ctx.audio?.bell({ hz: 520 + item.index * 46, gain: 0.5, ratio: 2.41 });
    });

    this._tmp = new Vector3();
  }

  update(dt, state) {
    const { palette, gaze, camera } = this.ctx;
    this.brassMat.color.copy(palette.get('brass'));
    this.lampMat.color.copy(palette.get('lamp'));

    const gazed = gaze.current?.kind === 'telescope' ? gaze.current.data?.item : null;

    // Hold to look. Once the eye is at the glass the gaze is no longer on the
    // instrument, so the hold alone keeps it there; releasing lowers it.
    const nextActive = state.holding ? (gazed ?? this.active) : null;

    if (nextActive !== this.active) {
      if (nextActive) {
        this.ctx.bus.emit('telescope:enter', { item: nextActive, eye: nextActive.eye });
        this.ctx.audio?.bell({ hz: 1180, gain: 0.3, ratio: 3.17 });
      } else {
        this.ctx.bus.emit('telescope:exit', {});
      }
      this.active = nextActive;
    }

    // Warmth: near things, looked-at things and remembered things glow.
    const best = [null, null];
    const bestD = [Infinity, Infinity];
    for (const it of this.items) {
      let want = 0;
      const d = this._tmp.copy(it.group.position).distanceTo(camera.position);
      if (d < 30) want = 0.1 * (1 - d / 30);
      if (it.remembered) want = Math.max(want, 0.25);
      if (it === gazed) want = Math.max(want, 0.35 + gaze.progress * 0.75);
      if (it === this.active) want = 1.1;
      it.warmth += (want - it.warmth) * Math.min(1, dt * 6);

      const colour = palette.get('memory');
      it.lens.material.color.copy(colour);
      it.lens.material.opacity = 0.25 + it.warmth * 0.7;
      it.lens.scale.setScalar(1 + it.warmth * 0.18);
      // You cannot see your own objective while you are looking through it.
      it.lens.visible = it !== this.active || state.iris < 0.25;
      it.halo.material.color.copy(palette.get('lamp'));
      it.halo.scale.setScalar(1 + Math.sin(state.elapsed * 1.3 + it.index) * 0.07 + it.warmth * 0.5);
      it.halo.material.opacity = 0.1 + it.warmth * 0.14;

      // A slow breathing of the mount, so nothing is quite still.
      it.group.position.y = it.baseY + Math.sin(state.elapsed * 0.45 + it.index * 1.7) * 0.004;

      if (d < bestD[0]) {
        bestD[1] = bestD[0];
        best[1] = best[0];
        bestD[0] = d;
        best[0] = it;
      } else if (d < bestD[1]) {
        bestD[1] = d;
        best[1] = it;
      }
    }

    this.lights.forEach((light, i) => {
      const it = best[i];
      if (!it) {
        light.intensity = 0;
        return;
      }
      light.position.copy(it.lamp.position);
      light.color.copy(palette.get('lamp'));
      const fall = 1 - Math.min(1, bestD[i] / 30);
      light.intensity = (13 + it.warmth * 24) * fall * fall;
    });

    // Per-telescope emissive is shared, so drive it from the strongest.
    const hottest = this.items.reduce((a, b) => (b.warmth > a.warmth ? b : a), this.items[0]);
    this.brassMat.emissive.copy(palette.get('lamp')).multiplyScalar(hottest.warmth * 0.16);
  }

  stats() {
    return {
      telescopes: this.items.length,
      aimed: this.items.filter((i) => i.outward).length,
      lifted: this.active ? this.active.index : '—',
      remembered: this.items.filter((i) => i.remembered).length,
    };
  }
}

/**
 * Every place on the stair where an instrument could actually see out.
 *
 * The stair and the arcade do not keep step: a turn of the stair is 21 m and
 * a storey is 10.8, so a bay's arch and the tread in front of it drift past
 * one another. Rather than shove a telescope at a wall and hope, this
 * enumerates the crossings — for each bay, the eleven heights at which the
 * stair faces it — and keeps only those where the eye lands inside the arch.
 *
 * The arch runs from about 3.1 m below the recorded opening centre to about
 * 1.6 m above it, so the usable band is asymmetric.
 */
function windowStations(helix, openings, cfg) {
  const stations = [];
  if (!openings.length) return stations;

  const period = 1 / helix.turns; // one revolution, in journey units
  const BELOW = 2.5; // how far under the centre the eye may sit
  const ABOVE = 1.1; // and how far over it

  for (const o of openings) {
    const angle = Math.atan2(o.z, o.x);
    // Solve angleAt(t) ≡ angle (mod 2π) for t, then walk the turns.
    let t =
      ((angle - helix.phase) / (helix.handed * helix.turns * Math.PI * 2)) % period;
    while (t < 0) t += period;
    for (; t <= 1; t += period) {
      if (t < cfg.firstAt || t > cfg.lastAt) continue;
      const residual = helix.heightAt(t) + 1.6 - o.y;
      if (residual < -BELOW || residual > ABOVE) continue;
      stations.push({ t, opening: o, residual: Math.abs(residual) });
    }
  }
  stations.sort((a, b) => a.t - b.t);
  return stations;
}

/** Spread `want` stations as evenly down the descent as the stair allows. */
function chooseStations(stations, want, cfg) {
  if (!stations.length || want <= 0) return [];
  const chosen = [];
  const used = new Set();
  for (let i = 0; i < want; i++) {
    const target = cfg.firstAt + ((i + 0.5) / want) * (cfg.lastAt - cfg.firstAt);
    let best = -1;
    let bestScore = Infinity;
    for (let j = 0; j < stations.length; j++) {
      if (used.has(j)) continue;
      // Near the wanted depth first; among equals, the squarest alignment.
      const score = Math.abs(stations[j].t - target) * 20 + stations[j].residual * 0.08;
      if (score < bestScore) {
        bestScore = score;
        best = j;
      }
    }
    if (best < 0) break;
    used.add(best);
    chosen.push(stations[best]);
  }
  chosen.sort((a, b) => a.t - b.t);
  return chosen;
}
